import { v4 as uuidv4 } from "uuid";
import { Box, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { CustomizedBox } from "components/Custom/CustomizedBox";
import CustomizedButton from "components/Custom/CustomizedButton";
import ExpensesListTable from "components/Table/Logistic/ExpensesList";
import Confirmation from "components/UI/Confirmation";
import ItemTableModal from "components/UI/Modal/CheckedModal/ItemTableModal";
import { useModal } from "hooks/use-modal";
import { useRef, useState, useEffect, useMemo } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IItem } from "types/Inventory/item";
import { IExpensesList } from "types/Logistic";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import PostDiscountAmount from "components/Table/Logistic/ExpensesList/PostDIscountAmount";
import { vatItemTypeFormatter } from "utils/Formatter/Global";
import { useDisable } from "hooks/use-disable";
import ExpnesesSummary from "./ExpensesSummary";
import { DriverNo } from "generated/sales";
import { useExpensePresets } from "hooks/Logistic/DeliveryTrip/use-expense-presets";

const ExpensesList = () => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { control, setValue, getValues } = useFormContext<IDeliveryTrip>();
  const { t } = useTranslation();
  const [disabled] = useDisable();

  const primaryDriverName = useWatch({
    control,
    name: "primary_driver.name",
  });

  const secondaryDriverName = useWatch({
    control,
    name: "secondary_driver.name",
  });

  const hasDriver1 = !!primaryDriverName;
  const hasDriver2 = !!secondaryDriverName;

  // 86d37db3t — payment_date no longer locks anything else. Add button gates
  // on View Mode (`disabled`) only.

  const {
    fields,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: "expenses_list",
  });

  const {
    primaryExpenses,
    secondaryExpenses,
    primaryIndexMap,
    secondaryIndexMap,
  } = useMemo(() => {
    const primary: any[] = [];
    const secondary: any[] = [];
    const primaryMap: number[] = [];
    const secondaryMap: number[] = [];

    fields.forEach((field, globalIndex) => {
      const driverNo = (field as any).driver_no as DriverNo | undefined;
      const normalized: DriverNo = driverNo ?? DriverNo.Primary;

      const extendedField = { ...(field as any), _globalIndex: globalIndex };

      if (normalized === DriverNo.Primary) {
        primary.push(extendedField);
        primaryMap.push(globalIndex);
      } else {
        secondary.push(extendedField);
        secondaryMap.push(globalIndex);
      }
    });

    return {
      primaryExpenses: primary,
      secondaryExpenses: secondary,
      primaryIndexMap: primaryMap,
      secondaryIndexMap: secondaryMap,
    };
  }, [fields]);

  const dragEndHandlerDriver1 = (result: DropResult) => {
    if (!result.destination) return;
    const fromGlobal = primaryIndexMap[result.source.index];
    const toGlobal = primaryIndexMap[result.destination.index];
    if (fromGlobal === undefined || toGlobal === undefined) return;
    move(fromGlobal, toGlobal);
  };

  const dragEndHandlerDriver2 = (result: DropResult) => {
    if (!result.destination) return;
    const fromGlobal = secondaryIndexMap[result.source.index];
    const toGlobal = secondaryIndexMap[result.destination.index];
    if (fromGlobal === undefined || toGlobal === undefined) return;
    move(fromGlobal, toGlobal);
  };

  const [openDeleteItemConfirmation, setOpenDeleteItemConfirmation] =
    useState<boolean>(false);

  const [deletedIndex, setDeletedIndex] = useState<number | undefined>(
    undefined
  );

  const openDeleteItemConfirmationHandler = (globalIndex: number) => {
    setOpenDeleteItemConfirmation(true);
    setDeletedIndex(globalIndex);
  };

  const closeDeleteItemConfirmationHandler = () => {
    setOpenDeleteItemConfirmation(false);
    setDeletedIndex(undefined);
  };

  const deleteItemConfirmationAction = () => {
    if (deletedIndex || deletedIndex === 0) {
      remove(deletedIndex);
      // setItemIds((prev) => prev.filter((_, index) => index !== deletedIndex));
      // setItemIdsSnapshot((prev) =>
      //   prev.filter((_, index) => index !== deletedIndex)
      // );
    }
    closeDeleteItemConfirmationHandler();
  };

  const openDeleteDriver1Handler = (localIndex: number) => {
    const globalIndex = primaryIndexMap[localIndex];
    if (globalIndex === undefined) return;
    openDeleteItemConfirmationHandler(globalIndex);
  };

  const openDeleteDriver2Handler = (localIndex: number) => {
    const globalIndex = secondaryIndexMap[localIndex];
    if (globalIndex === undefined) return;
    openDeleteItemConfirmationHandler(globalIndex);
  };

  const {
    modal: itemModal,
    openModalHandler: openItemModalHandler,
    closeModalHandler: closeItemModalHandler,
  } = useModal();

  const [activeDriverForAdd, setActiveDriverForAdd] = useState<1 | 2>(1);

  const openAddItemForDriver = (driverNo: 1 | 2) => {
    setActiveDriverForAdd(driverNo);
    openItemModalHandler();
  };

  const addItemsHandler = () => {
    const selectedItems = gridRef?.current?.api.getSelectedRows();
    if (selectedItems) {
      selectedItems.forEach((item_details: IItem) => {
        // 86d37db3t: SP00015 (ค่าเบิกล่วงหน้า) joins the 0%-withholding set.
        const isZeroWithholding: boolean = [
          "SP00009",
          "SP00010",
          "SP00011",
          "SP00012",
          "SP00013",
          "SP00014",
          "SP00015",
        ].includes(item_details.unique_id);

        const driverNo: DriverNo =
          activeDriverForAdd === 1 ? DriverNo.Primary : DriverNo.Secondary;

        const formatItems: IExpensesList = {
          unique_id: uuidv4(),
          item_id: item_details.id || 0,
          item_unique_id: item_details.unique_id || "",
          item_name: item_details.name || "",
          item_sku_desc: item_details.description,
          qty: 1,
          uom_id: item_details.stock_uom_id || 0,
          uom_name: item_details.stock_uom_name || "",
          uom_conversion_list: item_details.uom_conversion_list,
          discount: 0,
          pre_vat_amount: 0,
          price_per_unit: item_details.purchase_price ?? 0,
          withholding_tax_type: isZeroWithholding ? "0" : "3",
          withholding_tax_value: 0,
          vat_percentage: vatItemTypeFormatter(
            item_details.purchase_vat_type || ""
          ),
          driver_no: driverNo,
          net_amount: 0,
          withholding_tax_amount: 0,
          total_amount: 0,
        };

        append(formatItems);
      });
    }
    closeItemModalHandler();
  };

  // 86d4889jb / 86d4b1jdw / 86d4b1mkf — one reconciler in place of the two
  // "did the name just change" effects that used to live here.
  //
  // Rows belong to a driver. When that driver is absent the rows must not
  // exist — a hidden row is still submitted and still counted by the expenses
  // report (AC16) — and when the same driver comes back, the rows the user
  // had typed should come back with them rather than fresh blanks.
  //
  // Keyed on the *current* state (is there a driver, are there rows) and not
  // on a transition this component happened to observe, so it does not
  // matter how the driver went away — picker, ประเภทรถ change, vehicle
  // change, copy, a `reset()` upstream — nor whether this component was
  // mounted at the time. And each driver's rows are only ever removed when
  // *that* driver is absent: with driver 2 present, nothing here can drop
  // driver 2's rows, whatever happens to driver 1.
  const driver1SnapshotRef = useRef<IExpensesList[] | null>(null);
  const driver2SnapshotRef = useRef<IExpensesList[] | null>(null);

  // A driver with no rows and nothing to restore gets the preset rows. That
  // covers the new document, the copy (drivers kept, rows dropped) and — the
  // case this CR exists for — a saved booking that gets its driver assigned
  // later. Seeding waits for the item master so ids are right; the static
  // fallback only kicks in if the master cannot be read. Not in View Mode:
  // the form must not show rows the document does not have.
  const { presetsReady, buildPresets } = useExpensePresets();

  // Presets are offered once per driver: a driver who has (or had) rows in
  // this session does not get them back by deleting every row — that is the
  // user's decision. The flag drops when the driver is removed, so a driver
  // picked afresh is seeded again.
  const seededRef = useRef({ primary: false, secondary: false });

  useEffect(() => {
    const current = getValues("expenses_list") || [];
    const isSecondary = (row: IExpensesList) =>
      row.driver_no === DriverNo.Secondary;
    // Rows saved before `driver_no` existed carry null. The table already
    // shows them under driver 1 (`driverNo ?? Primary` above), so they are
    // driver 1's here as well — a strict `=== Primary` would silently drop
    // them on the first driver change.
    const primaryRows = current.filter((row) => !isSecondary(row));
    const secondaryRows = current.filter(isSecondary);

    let nextPrimary = primaryRows;
    let nextSecondary = secondaryRows;
    let changed = false;

    if (!hasDriver1) {
      seededRef.current.primary = false;
      if (primaryRows.length > 0) {
        driver1SnapshotRef.current = primaryRows;
        nextPrimary = [];
        changed = true;
      }
    } else if (primaryRows.length > 0) {
      seededRef.current.primary = true;
    } else if (
      driver1SnapshotRef.current &&
      driver1SnapshotRef.current.length > 0
    ) {
      nextPrimary = driver1SnapshotRef.current.map((row) => ({
        ...row,
        driver_no: DriverNo.Primary,
      }));
      driver1SnapshotRef.current = null;
      seededRef.current.primary = true;
      changed = true;
    } else if (presetsReady && !disabled && !seededRef.current.primary) {
      nextPrimary = buildPresets(DriverNo.Primary);
      seededRef.current.primary = true;
      changed = nextPrimary.length > 0;
    }

    if (!hasDriver2) {
      seededRef.current.secondary = false;
      if (secondaryRows.length > 0) {
        driver2SnapshotRef.current = secondaryRows;
        nextSecondary = [];
        changed = true;
      }
    } else if (secondaryRows.length > 0) {
      seededRef.current.secondary = true;
    } else if (
      driver2SnapshotRef.current &&
      driver2SnapshotRef.current.length > 0
    ) {
      nextSecondary = driver2SnapshotRef.current.map((row) => ({
        ...row,
        driver_no: DriverNo.Secondary,
      }));
      driver2SnapshotRef.current = null;
      seededRef.current.secondary = true;
      changed = true;
    } else if (presetsReady && !disabled && !seededRef.current.secondary) {
      nextSecondary = buildPresets(DriverNo.Secondary);
      seededRef.current.secondary = true;
      changed = changed || nextSecondary.length > 0;
    }

    if (changed) {
      setValue("expenses_list", [...nextPrimary, ...nextSecondary]);
    }
    // `fields` is in the deps so the check re-runs after any write to the
    // list, whoever made it; it only writes when something is actually out
    // of line, so it settles in one pass.
  }, [
    hasDriver1,
    hasDriver2,
    fields,
    presetsReady,
    buildPresets,
    disabled,
    getValues,
    setValue,
  ]);

  // 86d4889jb — no driver at all means no rows exist (see the reconciler
  // above), so there is nothing to show and an empty card would just look
  // broken.
  if (!hasDriver1 && !hasDriver2) return null;

  return (
    // 86d4b1jhp — top margin only. The page stacks these cards in a flex
    // column, so sibling margins add instead of collapsing, and the cards
    // around this one alternate 16/0 to keep every gap at 16px. This card can
    // disappear (no driver), which broke that alternation: with it gone,
    // น้ำมัน (0 below) met Appa Daily Worksheet (0 above) with no gap at all
    // — the "cards touching" of 86d488a5h Part B — and the fix for that gave
    // Daily Worksheet a top margin, which then doubled up with this card's
    // bottom margin when it *was* shown. Owning only the top margin here
    // makes the gap 16px whether or not this card renders.
    <CustomizedBox maxWidth={1650} margin={"1rem 0rem 0rem 0rem"}>
      <Typography fontWeight={600} mb={1}>
        {t("logistic.expenses_list.index")}
      </Typography>
      {hasDriver1 && (
        <>
          <Box display="flex" gap={0.5} alignItems="center" mb={2}>
            <Typography fontWeight={400}>
              พนักงานขับรถ 1 :
            </Typography>
            <Typography fontWeight={600}>
              {primaryDriverName}
            </Typography>
          </Box>
          <ExpensesListTable
            fields={primaryExpenses}
            dragEndHandler={dragEndHandlerDriver1}
            openDeleteItemConfirmationHandler={(index: number) =>
              openDeleteDriver1Handler(index)
            }
          />
          <Box mt={2}>
            {!disabled && (
              <CustomizedButton
                startAddIcon
                variant="outlined"
                title={t("logistic.expenses_list.add")}
                onClick={() => openAddItemForDriver(1)}
              />
            )}
          </Box>
          <Box mt={2}>
            <PostDiscountAmount source="driver1" />
            <ExpnesesSummary source="driver1" />
          </Box>
        </>
      )}

      {hasDriver2 && (
        <>
          <Box display="flex" gap={0.5} alignItems="center" mb={2}>
            <Typography fontWeight={400}>
              พนักงานขับรถ 2 :
            </Typography>
            <Typography fontWeight={600}>
              {secondaryDriverName}
            </Typography>
          </Box>
          <ExpensesListTable
            fields={secondaryExpenses}
            dragEndHandler={dragEndHandlerDriver2}
            openDeleteItemConfirmationHandler={(index: number) =>
              openDeleteDriver2Handler(index)
            }
          />
          <Box mt={2}>
            {!disabled && (
              <CustomizedButton
                startAddIcon
                variant="outlined"
                title={t("logistic.expenses_list.add")}
                onClick={() => openAddItemForDriver(2)}
              />
            )}
          </Box>
          <Box mt={2}>
            <PostDiscountAmount source="driver2" />
            <ExpnesesSummary source="driver2" />
          </Box>
        </>
      )}

      {(hasDriver2) && (
        <Box
          sx={{
            minHeight: 134,
            borderRadius: 2,
            py: 0.5,
          }}
          bgcolor={"#F2F2F266"}
          minHeight={134}
          justifyItems="flex-end"
        >
          <Typography fontWeight={600} sx={{ ml: 2.5, mr: 4, mt: 1 }}>
            สรุปรวม
          </Typography>
          <PostDiscountAmount source="all" noBg={true} />
          <ExpnesesSummary source="all" />
        </Box>
      )}

      <Confirmation
        title="ลบรายรับพนักงานขับรถ"
        open={openDeleteItemConfirmation}
        handleClose={closeDeleteItemConfirmationHandler}
        action={deleteItemConfirmationAction}
      />
      <ItemTableModal
        ref={gridRef}
        itemModal={itemModal}
        closeItemModalHandler={closeItemModalHandler}
        addItemsHandler={addItemsHandler}
        isService
        isExpenses
      />
    </CustomizedBox>
  );
};

export default ExpensesList;
